import { useContext, useEffect, useState } from "react"
import { Spinner, Table } from "react-bootstrap";
import { ESInterface, UTMGridData, UTMGridItem } from "./ESInterface";
import { ExploreSearchContext, removeFilter, addFilter, FilterType } from "../states/ExploreSearchState";
import { BBOXTURF, EmbeddedVectorMap, MapState } from "./EmbeddedVectorMap";
import proj4 from 'proj4';
import * as turf from '@turf/turf';
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { DataCollector, DC } from "src/dataCollector/DataCollector";
import { AppMessagesContext, Localization, SettingsManager, actionClearProgressMessage, actionSetErrorMessage, actionSetInfoMessage, actionSetProgressMessage } from "@viamap/viamap2-common";
import { ProjectContext, handleToBeAdded } from "src/states/ProjectState";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { Feature } from "src/states/ApplicationStateFeatures";
import { HeaderWithOpenCloseChevron } from "src/components/ComponentUtils";

import { ExportData } from "src/managers/ExportData";
import { SheetFunc } from "src/managers/SheetFunc";
import { ViewButtonCaret, ViewButtonIcon, ViewButtonPopDown, ViewButtonText } from "src/propertyInfoTemplates/PropertyComponents";
import { actionAddDataLayer, MapitStateContext } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { ApplicationStateContext } from "src/states/ApplicationState";
import intersect from "@turf/intersect";
import { GlassCard, GlassCheckbox } from "src/components/MitGlassComponents";
import { message } from "src/components/Message";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { PropertiesToExcel } from "src/managers/PropertyExcel";
import { GlassButton } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

export const Results = (props: { showClose: boolean, callBackOnClose: () => void, selectBFECallback: (a: number, b: [number, number]) => void }) => {

   const zoomLevelShowingItemsInsteadOfGroups = 8;
   let { state: projState, dispatch: projDispatch } = useContext(ProjectContext);
   let { dispatch: windowDispatch } = useWindowContext()
   let [selectedInfo, setSelectedInfo] = useState<any>({})

   const { dispatch: mapitDispatch } = useContext(MapitStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   let { state: explorerSearchState, dispatch } = useContext(ExploreSearchContext);
   let [loading, setLoading] = useState<boolean>(false);
   let [matchCount, setMatchCount] = useState<number>();
   let [countsToDisplay, setCountsToDisplay] = useState<UTMGridData>([]);
   let [filterJSON, setFilterJson] = useState<any>(combinedFilterJson([explorerSearchState.activeFilters["drawn_area"], explorerSearchState.activeFilters["polygon"], explorerSearchState.activeFilters["map_layer"]]))
   let [bboxItemsDisplay, setBboxItemsToDisplay] = useState<any[]>([]);
   let [mapState, setMapState] = useState<MapState>();
   let [showListView, setShowListView] = useState<boolean>(false);
   let [listItemsDisplay, setListItemsToDisplay] = useState<any[]>([]);
   const listViewMaxRows = SettingsManager.getSystemSetting("explore.listViewMaxRows");
   const maxExcelRows = SettingsManager.getSystemSetting("explore.downloadToExcelMaxRows")
   const addToProjectMaxRows = SettingsManager.getSystemSetting("explore.addToProjectMaxRows");

   const elasticIndex = SettingsManager.getSystemSetting("indexNamePropertySearch");

   useEffect(() => {
      let ifx = new ESInterface(elasticIndex);
      setLoading(true);
      ifx.doCount(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters)
         .then((count) => {
            setMatchCount(count);
         })
         .catch((err) => {
            setMatchCount(0);
            appMessageDispatch(actionSetErrorMessage("Got error:" + (err.message || err)))
         })
         .finally(() => {
            setLoading(false);
         })
      ifx.doGetGroups(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters)
         .then((items: UTMGridData) => {
            setCountsToDisplay(items);
         })
         .catch((err) => {
            setCountsToDisplay([]);
            appMessageDispatch(actionSetErrorMessage("Got error:" + (err.message || err)))
         })
      // Handle showing filterArea on the map
      setFilterJson(combinedFilterJson([explorerSearchState.activeFilters["drawn_area"], explorerSearchState.activeFilters["polygon"], explorerSearchState.activeFilters["map_layer"]]))

      if (mapState && mapState.zoom > zoomLevelShowingItemsInsteadOfGroups) {
         getDataForBBOX(mapState.bbox || []);
      }
   }, [explorerSearchState.activeFilters]);

   function handleSelect(a: number, b: [number, number]) {
      setSelectedInfo({ bfe_nr: a, coords: a })
      props.selectBFECallback(a, b)
   }

   useEffect(() => {
      if (mapState && mapState.zoom > zoomLevelShowingItemsInsteadOfGroups) {
         getDataForBBOX(mapState.bbox || []);
      }
   }, [mapState]);


   useEffect(() => {
      if (showListView && matchCount) {
         getDataForList();
      }

   }, [showListView, matchCount])

   function callbackOnMapMove(mapState: MapState) {
      setMapState(mapState);
   }

   function transform2GeoJsonGrid(data: UTMGridData): any {
      return data ? {
         "type": "FeatureCollection",
         "features": data.map((ugi: UTMGridItem) => {
            let { lng, lat } = convertFromUTMtoLatLng({ x: ugi.centerUTM32_easting, y: ugi.centerUTM32_northing });
            return {
               type: "Feature",
               properties: {
                  doc_count: ugi.doc_count,
                  key: ugi.key
               },
               geometry: {
                  "type": "Point",
                  "coordinates": [lng, lat]
               }
            }
         })
      } : {};
   }

   function transform2GeoJsonItem(data: any): any {
      return data ? {
         "type": "FeatureCollection",
         "features": data.map((item: any) => {
            return {
               type: "Feature",
               properties: {
                  ...item,
                  SFEnummer: item.bfe_sfe_nr
               },
               geometry: {
                  "type": "Point",
                  "coordinates": [item.koord_lng, item.koord_lat]
               }
            }
         }).filter((a: any) => a.geometry.coordinates[0] && a.geometry.coordinates[1])
      } : {};
   }


   function convertFromUTMtoLatLng(utm: { x: number, y: number }): { lat: number, lng: number } {
      let wgs84 = '+proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees';
      let utm32 = '+proj=utm +zone=32 +ellps=GRS80 +units=m +no_defs';
      let coords = proj4(utm32, wgs84, [utm.x, utm.y]);
      return { lat: coords[1], lng: coords[0] };
   }

   function TilføjHandler(data: Promise<any>) {
      data.then(async (obj) => {
         let EBR = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([obj.map((a) => a.bfe_nr.toString())]).then((res) => {
            return res.map((singleRes) => ({
               bfeNr: parseInt(singleRes.bfeNr),
               name: singleRes.betegnelse || singleRes.bfeNr,
               latLng: [0, 0] as [number, number],
            }))
         }
         )
         projDispatch(handleToBeAdded(EBR.filter((a) => a)))
      }).catch(() => {
         console.error("Errr")
      })
   }

   async function handleAddToMap(name: string, asSFE: boolean = false) {
      function NoTranslationRemoval(obj: { [key: string]: any }) {
         let res = {}
         Object.keys(obj).forEach((a) => {
            if (!a.includes("No translation found for")) {
               res[a] = obj[a]
            }
         })
         return res
      }

      let ifx = new ESInterface(elasticIndex);
      ifx.doSearch(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters, maxExcelRows).then(async (obj) => {
         let ExportInstance = new ExportData()
         let nextLayer;
         if (asSFE) {
            async function fastGeometries(bfeNR: string[]) {
               return await DC.Utils.paginateWFS(bfeNR, 1000, async (page, idx) => {
                  return await DC.WFS.getFeatureQuery("Matriklen2", "SamletFastEjendom_Gaeldende",
                     `BFEnummer in (${page.map((a) => `'${a}'`).join(",")})`
                  ) as { features: any[] }
               }, 5);
            }

            let SFEnr = obj.filter((a: any) => {
               return a.bfe_ejendomsType === "SamletFastEjendom";
            }).map((a: any) => a.bfe_nr.toString());

            let FC = await fastGeometries(SFEnr)
            let features = FC.features.map((feature: any) => {
               let sfeProperties = obj.find((a: any) => a.bfe_nr.toString() == feature.properties.BFEnummer);
               console.log("SFE properties", sfeProperties)
               let combinedProps = {
                  SFEnummer: sfeProperties.bfe_nr,
                  bfe_nr: sfeProperties.bfe_nr,
                  bfe_ejendomsType: sfeProperties.bfe_ejendomsType,
                  bfe_adresse: sfeProperties.bfe_adresse,
                  jords_matrikelnr: sfeProperties.jords_matrikelnr,
                  jords_ejerlavskode: sfeProperties.jords_ejerlavskode,
                  sfe_registreretArealSamlet: sfeProperties.sfe_registreretArealSamlet,
                  poi_dist_stop: sfeProperties.poi_dist_stop
               };
               let formatedProps = ExportInstance.filterAndFormatProps(combinedProps, explorerSearchState.selectableFilterList);
               return {
                  ...feature,
                  properties: formatedProps
               }
            });
            nextLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
               { features: features },
               name || "Added SFEs From Explorer"
            )
            nextLayer.propertiesToDisplay = Object.keys(ExportInstance.filterAndFormatProps({
               SFEnummer: "",
               bfe_nr: "", bfe_ejendomsType: "", bfe_adresse: "", jords_matrikelnr: "", jords_ejerlavskode: "", sfe_registreretArealSamlet: "",
               poi_dist_stop: "", jords_RegistreretAreal: "", jords_VejAreal: ""
            }, explorerSearchState.selectableFilterList)).map((a) => { return { name: a } })
            mapitDispatch(actionAddDataLayer(
               nextLayer
               , false))
            appMessageDispatch(actionSetInfoMessage(Localization.getText("Search results (only SFEs) has been added to the map")))
         } else {
            nextLayer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(
               transform2GeoJsonItem(obj.map((item) => { return { ...NoTranslationRemoval(ExportInstance.filterAndFormatProps(item, explorerSearchState.selectableFilterList)), koord_lng: item.koord_lng, koord_lat: item.koord_lat, bfe_sfe_nr: item.bfe_sfe_nr } })),
               name || "Added From Explorer"
            )
            nextLayer.propertiesToDisplay = Object.keys(ExportInstance.filterAndFormatProps({
               SFEnummer: "",
               bfe_nr: "", bfe_ejendomsType: "", bfe_adresse: "", jords_matrikelnr: "", jords_ejerlavskode: "", sfe_registreretArealSamlet: "",
               poi_dist_stop: ""
            }, explorerSearchState.selectableFilterList)).map((a) => { return { name: a } })
            mapitDispatch(actionAddDataLayer(
               nextLayer
               , false))
            appMessageDispatch(actionSetInfoMessage(Localization.getText("Search results has been added to the map")))
         }
      }).catch((a) => {
         console.error("Error", a)
      })
   }

   async function handleAdvancedExcelExport(fileName: string, options?: Parameters<typeof PropertiesToExcel>[1]) {
      let ifx = new ESInterface(elasticIndex);
      function bfeType(params: string): "SFE" | "EJL" | "BPFG" {
         return { SamletFastEjendom: "SFE", Ejerlejlighed: "EJL", BygningPåFremmedGrund: "BPFG" }[params] as "SFE" | "EJL" | "BPFG" || "SFE"
      }

      let items = await ifx.doSearch(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters, maxExcelRows)
      let workbook = await PropertiesToExcel(
         items.map((a: any) => ({ bfe: a.bfe_nr, type: bfeType(a.bfe_ejendomsType) })),
         options ?? {}
      )
      let blob = SheetFunc.createBlob(workbook);
      ExportData.downloadBlob(fileName, blob);
   }

   async function handleExportExcel(fileName: string) {
      try {
         let createdDate = new Date();
         let ifx = new ESInterface(elasticIndex);
         ifx.doSearch(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters, maxExcelRows)
            .then(async (items) => {
               let count = items.length || 0;


               let wb = await (new ExportData()).createExcelExportForSearchResults(items, createdDate, explorerSearchState.selectableFilterList, explorerSearchState.activeFilters,
                  (progress: number) => {
                     appMessageDispatch(actionSetProgressMessage("Export", Math.round(100 * progress / count)));
                  },
                  hasAccessToFeature(Feature.ExportFloodingProperties)
               );
               appMessageDispatch(actionClearProgressMessage());
               let blob = SheetFunc.createBlob(wb);
               ExportData.downloadBlob(fileName, blob);
               appMessageDispatch(actionSetInfoMessage(Localization.getText("Search results has been exported to excel")));
            })
            .catch((err) => {
               appMessageDispatch(actionSetErrorMessage(err.message || JSON.stringify(err)));
            })
      }
      catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(error));
      }
   }

   function callbackOnUserPolygon(poly: any) {
      // alert("Got polygon callback "+JSON.stringify(poly));
      if (poly && poly.features && poly.features.length > 0) {
         let feature = poly.features[0];
         dispatch(addFilter("polygon", feature.geometry.coordinates));
      } else {
         dispatch(removeFilter("drawn_area"));
         dispatch(removeFilter("polygon"));
      }
   }

   function getDataForBBOX(bbox: BBOXTURF) {
      // Return items within the bbox
      let ifx = new ESInterface(elasticIndex);
      let bboxFilter = { "tmp_bbox": { title: "Temporary bbox", filterType: FilterType.BoundingBox, default: [11.68, 55.06, 12.29, 55.42] } };
      let bboxFilterValue = { "tmp_bbox": bbox };
      let tmpFilters: any = { ...explorerSearchState.selectableFilterList, ...bboxFilter } as any;
      let tmpFiltersValues = { ...explorerSearchState.activeFilters, ...bboxFilterValue };
      ifx.doSearch(tmpFilters, tmpFiltersValues, 4000)
         .then((items) => {
            setBboxItemsToDisplay(items);
         })
         .catch((err) => {
            setBboxItemsToDisplay([]);
            appMessageDispatch(actionSetErrorMessage("Got error:" + (err.message || err)))
         })
   }

   function getDataForList() {
      // Return items to display in list
      let ifx = new ESInterface(elasticIndex);
      ifx.doSearch(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters, listViewMaxRows)
         .then((items) => {
            setListItemsToDisplay(items);
         })
         .catch((err) => {
            setListItemsToDisplay([]);
            appMessageDispatch(actionSetErrorMessage("Got error:" + (err.message || err)))
         })
   }



   function ItemHitList(props: { items: any[] }) {
      const tableColumns = {
         "bfe_nr": "BFE nr",
         "bfe_ejendomsType": "Ejendomstype",
         // "koord_status":"Koord",
         "ejf_ejere": "Ejere",
         "ejf_cvr": "CVR"
      }
      const allPropsFilter = (prop: string) => {
         if (hasAccessToFeature(Feature.Debugging)) {
            // Show all 
            return true;
         } else {
            // Hide all 'technical' properties.
            return !(
               prop.startsWith("id_")
               || prop.startsWith("error_")
               || prop.startsWith("time_")
               || prop === "time"
               || prop.startsWith("bygningspunkt_")
               || prop.startsWith("import_")
               || ["time", "koord_status", "geopoint", "ejf_ejere_liste"].includes(prop)
            )
         }
      }

      function TableLine(props: { idx: number, item: any }) {
         let [expanded, setExpanded] = useState<boolean>(false)
         return (
            <tr key={props.idx}>
               <td>
                  <HeaderWithOpenCloseChevron
                     onClick={() => { setExpanded(!expanded) }}
                     isOpen={expanded}
                     title={""}
                  />
               </td>
               {!expanded ? (
                  <>
                     {
                        Object.keys(tableColumns).map((key, idx) => {
                           return (
                              <td key={idx}>{props.item[key]}</td>
                           );
                        })
                     }
                  </>
               ) : (
                  <td colSpan={Object.keys(tableColumns).length}>
                     <table>
                        <tbody>
                           <>
                              {
                                 Object.keys(props.item).filter(allPropsFilter).map((key, idx) => {
                                    let val = props.item[key];
                                    // Some properties may be an object or array
                                    if (
                                       ((typeof val === 'object')
                                          || Array.isArray(val)) &&
                                       val !== null
                                    ) {
                                       val = JSON.stringify(val, undefined, 3);
                                    }
                                    return (
                                       <tr key={idx}>
                                          <td>{key}</td>
                                          <td>{val}</td>
                                       </tr>
                                    );
                                 })
                              }
                           </>
                        </tbody>
                     </table>
                  </td>
               )
               }
            </tr>
         )
      }

      return (
         <div style={{}}>
            <Table striped bordered hover>
               <thead>
                  {(matchCount || 0) > listViewMaxRows ? (
                     <tr>
                        <th colSpan={Object.keys(tableColumns).length + 1} style={{ fontStyle: "italic", fontSize: "larger" }}>
                           {Localization.getFormattedText("Shows first {listViewMaxRows} rows", { listViewMaxRows })}
                        </th>
                     </tr>
                  ) : null}
                  <tr key={"x"}>
                     <>
                        <th></th>
                        {
                           Object.values(tableColumns).map((col, idx) => {
                              return (
                                 <th key={idx}>{col}</th>
                              );
                           })
                        }
                     </>
                  </tr>
               </thead>
               <tbody>
                  <>
                     {
                        props.items.map((itm, idx) => {
                           return (
                              <TableLine
                                 key={idx}
                                 idx={idx}
                                 item={itm}
                              />
                           );
                        })
                     }
                  </>
               </tbody>
            </Table>
         </div>
      )
   }

   return (
      <>
         <GlassCard
            variant={showListView ? "" : "noScroll GlassCard-FlexBody"}
            title={
               <>
                  <span>
                     <span>{Localization.getText("Result")}</span>
                     <span style={{ marginLeft: "2em", opacity: 0.75, fontSize: "16px" }}>{matchCount}</span>
                     {loading ? (
                        <Spinner size="sm" animation="border" variant="success" />
                     ) : null}
                  </span>
                  <div style={{ display: "flex", gap: "5px", flexWrap: "wrap", justifyContent: "flex-end", flex: "auto" }} >
                     <ProtectedFeature feature={Feature.ExplorerShowAsList} contentsIfNoAccess={<></>} >
                        <ViewButtonCaret
                           onClick={() => { setShowListView(!showListView) }}
                           isOpen={showListView}
                        >{Localization.getText("Show list")}</ViewButtonCaret>
                     </ProtectedFeature>
                     <ProtectedFeature feature={Feature.AddToProjectUnassignedList} contentsIfNoAccess={<></>} >
                        <>
                           {(projState.isLoaded ?
                              <>
                                 <ViewButtonText onClick={() => { TilføjHandler(Promise.resolve([selectedInfo])) }} disabled={!selectedInfo?.bfe_nr} >{Localization.getText("Add Selected")}</ViewButtonText>
                                 <ViewButtonText onClick={(() => { let ifx = new ESInterface(elasticIndex); TilføjHandler(ifx.doSearch(explorerSearchState.selectableFilterList, explorerSearchState.activeFilters, addToProjectMaxRows)) })} disabled={!(matchCount && matchCount <= addToProjectMaxRows)} >{Localization.getFormattedText("Add Search ({count} max)", { count: addToProjectMaxRows })}</ViewButtonText>
                              </>
                              : null)}
                        </>
                     </ProtectedFeature>
                     {props.showClose ? (
                        <ViewButtonText onClick={() => props.callBackOnClose()} disabled={!props.showClose} >{Localization.getText("Return to Search")}</ViewButtonText>
                     ) : null}
                     <ProtectedFeature feature={Feature.ExplorerToExcel} contentsIfNoAccess={<></>} >
                        <ViewButtonIcon
                           disabled={!((matchCount || 0) > 0 && (matchCount || 0) <= maxExcelRows)}
                           title={Localization.getText("Export results to Excel") + " max " + maxExcelRows} className="" onClick={() => {
                              let locale = Localization.getLocale()
                              windowDispatch(openWindow(
                                 WindowId.NameOnExport,
                                 {
                                    defaultNames: ["SearchResultExport_" + new Date().toLocaleDateString(locale) + "_" + new Date().toLocaleTimeString(locale)],
                                    onConfirm: (names) => handleExportExcel(names[0] + ".xlsx"),
                                    onClose: () => { }
                                 }
                              ))
                           }
                           } ><img src="./images/NewSecSVG/excel.svg"></img></ViewButtonIcon>
                     </ProtectedFeature>
                     <ProtectedFeature feature={Feature.ExplorerSaveAsNewLayer} contentsIfNoAccess={<></>} >
                        <ViewButtonIcon disabled={!((matchCount || 0) > 0 && (matchCount || 0) <= maxExcelRows)}
                           onClick={() => {
                              windowDispatch(openWindow(
                                 WindowId.NameOnExport,
                                 {
                                    defaultNames: [Localization.getText("Properties from Explorer")],
                                    onConfirm: (names) => handleAddToMap(names[0]),
                                    onClose: () => { }
                                 }
                              ))
                           }
                           }
                           title={Localization.getText("Add as layer")}
                        ><MIBIcon icon="addToMap" /></ViewButtonIcon>
                     </ProtectedFeature>
                     <ProtectedFeature feature={Feature.PropertyExcel} contentsIfNoAccess={<></>} >
                        <PropertyExcelOptions current={matchCount || 0} confirm={(options) => {
                           let locale = Localization.getLocale()
                           windowDispatch(openWindow(
                              WindowId.NameOnExport,
                              {
                                 defaultNames: ["SearchResultExport_" + new Date().toLocaleDateString(locale) + "_" + new Date().toLocaleTimeString(locale)],
                                 onConfirm: (names) => handleAdvancedExcelExport(names[0] + ".xlsx", options),
                                 onClose: () => { }
                              }
                           ))
                        }
                        } ></PropertyExcelOptions>
                     </ProtectedFeature>
                     <ProtectedFeature feature={Feature.RawExplorer} contentsIfNoAccess={<></>} >
                        <ViewButtonIcon title={"Stevns Export"} onClick={() => {
                           let possible = structuredClone(explorerSearchState.selectableFilterList)
                           let active = structuredClone(explorerSearchState.activeFilters)
                           windowDispatch(openWindow(
                              WindowId.NameOnExport,
                              {
                                 defaultNames: [Localization.getText("Properties from Explorer")],
                                 onConfirm: (names) => ExportData.fastDownload(names[0], possible, active, true),
                                 onClose: () => { }
                              }
                           ))
                        }
                        }
                        ><MIBIcon icon="Stevns" /></ViewButtonIcon>
                     </ProtectedFeature>
                     <ProtectedFeature feature={Feature.ExplorerSaveAsNewLayer} contentsIfNoAccess={<></>} >
                        <ViewButtonIcon disabled={!((matchCount || 0) > 0 && (matchCount || 0) <= maxExcelRows)}
                           onClick={() => {
                              windowDispatch(openWindow(
                                 WindowId.NameOnExport,
                                 {
                                    defaultNames: [Localization.getText("Properties from Explorer (SFE)")],
                                    onConfirm: (names) => handleAddToMap(names[0], true),
                                    onClose: () => { }
                                 }
                              ))
                           }
                           }
                           title={Localization.getText("Add as SFE layer")}
                        ><MIBIcon icon="addToMapSFE" /></ViewButtonIcon>
                     </ProtectedFeature>
                  </div>
               </>
            }>

            <div className="ResultInfo">

            </div>
            {showListView ? (
               <ItemHitList items={listItemsDisplay} />
            ) : (
               <div className="MapWrapper">
                  <EmbeddedVectorMap
                     orthoPhoto={false}
                     dataSourceGeoJSON={transform2GeoJsonGrid(countsToDisplay)}
                     onPolygon={(poly) => callbackOnUserPolygon(poly)}
                     onMove={callbackOnMapMove}
                     selectBFE={handleSelect}
                     dataSourceFilterJSON={filterJSON}
                     dataSourceItemsJSON={transform2GeoJsonItem(bboxItemsDisplay)}
                     cadasters={false}
                     selectedItems={[]}
                  />
               </div>
            )}
         </GlassCard >
      </>
   )
}

function PropertyExcelOptions(props: { current: number, confirm: (options: { ejendom: boolean, adresser: boolean, bbr: boolean, jordstykker: boolean, ejerskab: boolean, tingbog: boolean, handel: boolean }) => void }) {

   const [open, setOpen] = useState<boolean>(false)
   const [options, setOptions] = useState<{ ejendom: boolean, adresser: boolean, bbr: boolean, jordstykker: boolean, ejerskab: boolean, tingbog: boolean, handel: boolean }>({
      ejendom: true,
      adresser: true,
      bbr: false,
      jordstykker: false,
      ejerskab: true,
      tingbog: true,
      handel: true,
   })
   const max = SettingsManager.getSystemSetting("explore.downloadToExcelMaxRows")


   return (

      <ViewButtonPopDown title={Localization.getText("Property Excel")} open={open}
         onClick={() => setOpen(e => !e)}
         popdown={
            <div className="BasicPopdown" >
               <div style={{ color: "#000", fontWeight: "bold", marginInline: "0px 60px" }}>{Localization.getText("Property Excel")}</div>
               <div style={{ color: "#333" }}>{Localization.getText("Include:")}</div>
               <GlassCheckbox checked={options.adresser} onChange={(e) => setOptions({ ...options, adresser: e.target.checked })}> Adresser</GlassCheckbox>
               <GlassCheckbox checked={options.ejerskab} onChange={(e) => setOptions({ ...options, ejerskab: e.target.checked })}> Ejerskab</GlassCheckbox>
               <GlassCheckbox checked={options.jordstykker} onChange={(e) => setOptions({ ...options, jordstykker: e.target.checked })}> Jordstykker</GlassCheckbox>
               <GlassCheckbox checked={options.bbr} onChange={(e) => setOptions({ ...options, bbr: e.target.checked })}> BBR</GlassCheckbox>
               <GlassButton disabled={props.current > max} onClick={() => { setOpen(false); props.confirm(options); }}>{Localization.getText("Generate")} {Localization.getFormattedText("(Max {max})", { max })}</GlassButton>
            </div>
         } >
         <span style={{ paddingLeft: ".5ex" }}>+</span><img src="./images/NewSecSVG/excel.svg"></img>
      </ViewButtonPopDown >
   )
}


function combinedFilterJson(geometries: any[]) {
   const EmptyJSON = {
      "type": "FeatureCollection",
      "features": []
   };

   const valid = geometries.filter((a) => a) || []
   if (valid.length == 0) {
      return EmptyJSON
   }
   let outerBounds = valid.map((a) => {
      if (Array.isArray(a[0]?.[0]?.[0]))
         return turf.multiPolygon(a as any)
      return turf.polygon(a)
   }) || [EmptyJSON]
   if (outerBounds.length < 2) {
      return turf.featureCollection([...outerBounds as any])
   }
   let interSection = intersect(turf.featureCollection([...outerBounds as any]), { properties: { lineColor: "orange" } }) || EmptyJSON
   return turf.featureCollection([...outerBounds as any, interSection as any])
}